import { useState } from "react";

import { ApiFehler } from "../api/client";
import { useDokumentLoeschen, useDokumentZuruecknehmen, useNachreichungEntscheiden } from "../api/hooks";
import type { Anlage, Dokument, ExtrahierteFelder } from "../api/typen";
import { useAuth } from "../kontext/AuthKontext";
import { useToast } from "../kontext/ToastKontext";
import { FELD_LABEL } from "../lib/felder";
import { TYP_ICON, TYP_TEXT, dateigroesse, datum, kwp, zeitpunkt } from "../lib/format";
import { Hinweis, Knopf, Modal } from "./Basis";

/** Felder, die eine Nachreichung übernehmen kann. Die MaStR-Angaben pflegt die MaStR-Seite. */
const ANLAGEN_FELDER = ["leistung_kwp", "modultyp", "wechselrichter", "strasse", "plz", "ort", "zaehlernummer", "iban", "zerez_id"] as const;
const MASTR_FELDER = ["anzahl_module", "modulleistung_wp", "wechselrichterleistung_kw", "speicher_vorhanden", "speicher_kapazitaet_kwh", "inbetriebnahme_datum"] as const;
const MASTR_LABEL: Record<(typeof MASTR_FELDER)[number], string> = {
  anzahl_module: "Anzahl Module",
  modulleistung_wp: "Modulleistung (Wp)",
  wechselrichterleistung_kw: "Wechselrichterleistung (kW)",
  speicher_vorhanden: "Speicher vorhanden",
  speicher_kapazitaet_kwh: "Speicherkapazität (kWh)",
  inbetriebnahme_datum: "Inbetriebnahme",
};

type Vorschlagsfeld = { name: string; label: string; aktuell: string; vorschlag: string; abweichend: boolean; masterfeld: boolean };

function anzeige(wert: unknown, feld: string): string {
  if (wert === null || wert === undefined || wert === "") return "–";
  if (typeof wert === "boolean") return wert ? "ja" : "nein";
  if (feld === "leistung_kwp" && typeof wert === "number") return kwp(wert);
  if (feld === "inbetriebnahme_datum" && typeof wert === "string") return datum(wert);
  return String(wert);
}

export function vorschlagsfelder(anlage: Anlage, dokument: Dokument): Vorschlagsfeld[] {
  const daten = (dokument.extrahierte_daten ?? {}) as Partial<ExtrahierteFelder>;
  const liste: Vorschlagsfeld[] = [];
  for (const name of ANLAGEN_FELDER) {
    const roh = daten[name];
    if (roh === null || roh === undefined) continue;
    const aktuell = anlage[name];
    liste.push({
      name,
      label: FELD_LABEL[name] ?? name,
      aktuell: anzeige(aktuell, name),
      vorschlag: anzeige(roh, name),
      abweichend: aktuell !== roh,
      masterfeld: false,
    });
  }
  for (const name of MASTR_FELDER) {
    const roh = daten[name];
    if (roh === null || roh === undefined) continue;
    liste.push({ name, label: MASTR_LABEL[name], aktuell: "siehe MaStR-Seite", vorschlag: anzeige(roh, name), abweichend: true, masterfeld: true });
  }
  return liste;
}

function UebernehmenDialog({ anlage, dokument, schliessen }: { anlage: Anlage; dokument: Dokument; schliessen: () => void }) {
  const entscheiden = useNachreichungEntscheiden(anlage.id);
  const toast = useToast();
  const felder = vorschlagsfelder(anlage, dokument);
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
  const [fehler, setFehler] = useState<string | null>(null);
  const angemeldet = anlage.status === "angemeldet";

  function umschalten(name: string) {
    setGewaehlt((alt) => {
      const neu = new Set(alt);
      if (neu.has(name)) neu.delete(name);
      else neu.add(name);
      return neu;
    });
  }

  function uebernehmen() {
    setFehler(null);
    entscheiden.mutate(
      { dokumentId: dokument.id, entscheidung: { aktion: "uebernehmen", felder: [...gewaehlt], version: anlage.version } },
      {
        onSuccess: () => {
          toast.erfolg("Werte übernommen", `${gewaehlt.size} Feld(er) aus „${dokument.original_dateiname}“`);
          schliessen();
        },
        onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Übernehmen fehlgeschlagen"),
      },
    );
  }

  return (
    <Modal
      offen
      breit
      titel="Werte aus nachgereichter Datei übernehmen"
      beiSchliessen={schliessen}
      fuss={
        <>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
          <Knopf variante="primaer" disabled={gewaehlt.size === 0 || angemeldet} laedt={entscheiden.isPending} onClick={uebernehmen}>
            {gewaehlt.size} Feld(er) übernehmen
          </Knopf>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-neutral-600">
          Datei: <span className="font-semibold">{dokument.original_dateiname}</span>. Übernommen wird nur, was Sie ankreuzen. Übernommene Werte gelten danach als geprüft.
        </p>
        {angemeldet && (
          <Hinweis art="warnung">
            Die Anlage ist angemeldet. Werte können erst übernommen werden, wenn die Registrierung zurückgenommen wurde. „Nur ablegen“ ist weiterhin möglich.
          </Hinweis>
        )}
        {anlage.status === "final" && (
          <Hinweis art="info">Die Anlage ist bereits bestätigt. Geänderte Werte ggf. im MaStR und beim Netzbetreiber nachtragen.</Hinweis>
        )}
        {felder.length === 0 ? (
          <Hinweis art="info">Aus dieser Datei wurden keine Werte erkannt. Sie können sie nur ablegen.</Hinweis>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-neutral-200">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="w-10 px-3 py-2"><span className="sr-only">Übernehmen</span></th>
                  <th className="px-3 py-2">Feld</th>
                  <th className="px-3 py-2">Aktuell</th>
                  <th className="px-3 py-2">Vorschlag aus der Datei</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {felder.map((f) => (
                  <tr key={f.name} className={gewaehlt.has(f.name) ? "bg-neutral-50" : ""}>
                    <td className="px-3 py-2">
                      <input type="checkbox" aria-label={`${f.label} übernehmen`} checked={gewaehlt.has(f.name)} disabled={angemeldet} onChange={() => umschalten(f.name)} />
                    </td>
                    <td className="px-3 py-2 font-semibold">{f.label}</td>
                    <td className="px-3 py-2 text-neutral-600">{f.aktuell}</td>
                    <td className={`px-3 py-2 ${f.abweichend ? "font-semibold text-ampel-gelb" : "text-neutral-600"}`}>
                      {f.vorschlag}
                      {!f.abweichend && <span className="ml-2 text-xs font-normal text-neutral-400">gleich</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {felder.some((f) => f.abweichend && !f.masterfeld) && !angemeldet && (
          <button
            type="button"
            className="text-sm font-semibold underline"
            onClick={() => setGewaehlt(new Set(felder.filter((f) => f.abweichend).map((f) => f.name)))}
          >
            Alle abweichenden Felder auswählen
          </button>
        )}
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      </div>
    </Modal>
  );
}

/** Hinweis „neue Unterlagen“ mit den offenen Nachreichungen einer Anlage. Der Anmelder entscheidet je Datei. */
export function NachreichungenBanner({ anlage, beiAnsehen }: { anlage: Anlage; beiAnsehen: (dokumentId: number) => void }) {
  const entscheiden = useNachreichungEntscheiden(anlage.id);
  const toast = useToast();
  const [dialogFuer, setDialogFuer] = useState<Dokument | null>(null);
  const offen = (anlage.dokumente ?? []).filter((d) => d.nachreichung_status === "offen");
  if (offen.length === 0) return null;

  function ablegen(d: Dokument) {
    entscheiden.mutate(
      { dokumentId: d.id, entscheidung: { aktion: "zur_kenntnis" } },
      {
        onSuccess: () => toast.info("Abgelegt", `„${d.original_dateiname}“ bleibt angehängt, nichts wurde geändert.`),
        onError: (e) => toast.fehler(e, "Entscheidung fehlgeschlagen"),
      },
    );
  }

  return (
    <section aria-label="Neue Unterlagen" className="rounded-2xl border border-ampel-gelb/40 bg-ampel-gelbHell p-4">
      <h2 className="font-semibold text-ampel-gelb">Neue Unterlagen ({offen.length})</h2>
      <p className="text-sm text-neutral-700">Der Installateur hat Dateien nachgereicht. Es wurde nichts automatisch übernommen. Sie entscheiden.</p>
      <ul className="mt-3 space-y-2">
        {offen.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-white px-3 py-2">
            <div className="min-w-[12rem] flex-1">
              <p className="truncate text-sm font-semibold">{TYP_ICON[d.typ]} {TYP_TEXT[d.typ]} · {d.original_dateiname}</p>
              <p className="text-xs text-neutral-500">nachgereicht {zeitpunkt(d.nachgereicht_am)}</p>
            </div>
            <Knopf variante="geist" onClick={() => beiAnsehen(d.id)}>Ansehen</Knopf>
            {d.typ !== "foto" && <Knopf onClick={() => setDialogFuer(d)}>Werte übernehmen…</Knopf>}
            <Knopf onClick={() => ablegen(d)} disabled={entscheiden.isPending}>Nur ablegen</Knopf>
          </li>
        ))}
      </ul>
      {dialogFuer && <UebernehmenDialog anlage={anlage} dokument={dialogFuer} schliessen={() => setDialogFuer(null)} />}
    </section>
  );
}

function ZuruecknehmenDialog({ anlage, dokument, schliessen }: { anlage: Anlage; dokument: Dokument; schliessen: () => void }) {
  const zuruecknehmen = useDokumentZuruecknehmen(anlage.id);
  const loeschen = useDokumentLoeschen(anlage.id);
  const { benutzer } = useAuth();
  const toast = useToast();
  const [grund, setGrund] = useState("");
  const [endgueltig, setEndgueltig] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const final = anlage.status === "final";
  const istAdmin = benutzer?.rolle === "admin";

  function absenden() {
    setFehler(null);
    if (endgueltig) {
      loeschen.mutate(
        { dokumentId: dokument.id, bestaetigt: final },
        {
          onSuccess: () => {
            toast.erfolg("Dokument endgültig gelöscht", dokument.original_dateiname);
            schliessen();
          },
          onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Löschen fehlgeschlagen"),
        },
      );
      return;
    }
    zuruecknehmen.mutate(
      { dokumentId: dokument.id, grund: grund.trim(), bestaetigt: final },
      {
        onSuccess: () => {
          toast.erfolg("Dokument zurückgenommen", dokument.original_dateiname);
          schliessen();
        },
        onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Zurücknehmen fehlgeschlagen"),
      },
    );
  }

  return (
    <Modal
      offen
      titel={endgueltig ? "Dokument endgültig löschen" : "Dokument zurücknehmen"}
      beiSchliessen={schliessen}
      fuss={
        <>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
          <Knopf variante="gefahr" laedt={zuruecknehmen.isPending || loeschen.isPending} onClick={absenden}>
            {endgueltig ? "Endgültig löschen" : "Zurücknehmen"}
          </Knopf>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p>
          <span className="font-semibold">{dokument.original_dateiname}</span> verschwindet aus der Liste.{" "}
          {endgueltig ? "Datei und Vorschaubild werden endgültig gelöscht." : "Die Datei bleibt für das Protokoll gespeichert."}{" "}
          Werte, die aus diesem Dokument stammen, werden aus den übrigen Dokumenten neu ermittelt. Geprüfte Werte bleiben.
        </p>
        {final && <Hinweis art="warnung">Die Anlage ist bereits bestätigt. Beim Zurücknehmen können MaStR-Angaben wegfallen.</Hinweis>}
        <div>
          <label htmlFor="zurueck-grund" className="mb-1 block text-xs font-semibold">Grund (optional)</label>
          <input
            id="zurueck-grund"
            data-autofokus
            className="w-full rounded-lg border border-neutral-300 px-3 py-2"
            maxLength={500}
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            placeholder="z. B. falsches Dokument"
          />
        </div>
        {istAdmin && (
          <label className="flex items-start gap-3 rounded-lg border border-neutral-200 p-3">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-ampel-rot"
              checked={endgueltig}
              onChange={(e) => setEndgueltig(e.target.checked)}
            />
            <span>
              <span className="font-semibold">Datei endgültig löschen</span>
              <span className="block text-xs text-neutral-500">
                Gibt {dateigroesse(dokument.dateigroesse)} Speicher frei. Lässt sich nicht rückgängig machen.
              </span>
            </span>
          </label>
        )}
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      </div>
    </Modal>
  );
}

export function ZuruecknehmenKnopf({ anlage, dokument }: { anlage: Anlage; dokument: Dokument }) {
  const [offen, setOffen] = useState(false);
  const angemeldet = anlage.status === "angemeldet";
  return (
    <>
      <Knopf
        variante="gefahr"
        disabled={angemeldet}
        title={angemeldet ? "Die Anlage ist angemeldet. Bitte zuerst die Registrierung zurücknehmen." : undefined}
        onClick={() => setOffen(true)}
      >
        Dokument zurücknehmen
      </Knopf>
      {offen && <ZuruecknehmenDialog anlage={anlage} dokument={dokument} schliessen={() => setOffen(false)} />}
    </>
  );
}
